import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { addHours, addMinutes, subMinutes, parseISO, isToday, startOfDay, endOfDay } from 'date-fns';

import { setAlertsToBack, getAlertsToBack, getCalendar } from '../store/redux';

import apiGetOrdersToPrep from '../api/getOrdersToPrep';
import apiOrderWasSeen from '../api/orderWasSeen';

import { calendarUtils, Button, Modal } from '@fernleaf/util';

import classes from './Alerts.module.css';

const alertCache = {
    checkPending: false,
    lastPing: null,
    orders: {}
};

const getHoursForToday = ( calendar ) => {
    const now = new Date();
    const createTimeFrom = ( hhmm ) => {
        if ( hhmm ) {
            const hh = Math.trunc( hhmm / 100 );
            const mm = hhmm - ( hh * 100 );
            return addHours( addMinutes( startOfDay( now ), mm ), hh );
        }
        return null;
    }
    const [ open, close ] = calendar
        ? calendarUtils.getHoursForDate( new Date(), calendar ) // Returns [ HHMM, HHMM ]
        : [ null, null ];

    return {
        open:  createTimeFrom( open ),
        close: createTimeFrom( close )
    }
}

const sortMostUrgent = ( a, b ) => (
    a.pickupDateAndTime < b.pickupDateAndTime ? -1 :
    a.pickupDateAndTime > b.pickupDateAndTime ? +1 :
                                                 0
);

const dummySetFn = () => {};

const dummyDispatch = ( action ) => {
    console.log( "checkForAlerts/markOrderAsSeen - fetch failed:", action );
    // Usually the user navigated away during the fetch operation
}

const markOrderAsSeen = ( e ) => {
    e.stopPropagation();
    apiOrderWasSeen( e.target.closest( 'div' ).dataset.id, dummySetFn, dummyDispatch );
}

const checkOrders = ( orders, hours, setAlert, setDetailOrderId ) => {
    const messages = [];

    const ordersToPack = ( orders || [] )
        .filter( order => order.packStatus === 'To Pack' )
        .length;
    if ( ordersToPack ) {
        messages.push(
            <div key={ 'To Pack' } className={ classes.AlertMessage } >
                <div className={ classes.Overdue } >TO PACK:</div>
                There { ordersToPack === 1 
                    ? ' is 1 order '
                    : ` are ${ ordersToPack } orders ` 
                } 
                waiting to be packed.
            </div>
        )
    }
    const now = new Date();
    const fiveMinutesAgo = subMinutes( now, 5 );
    const plusFiveMinutes = addMinutes( now, 5 );
    const plusFifteenMinutes = addMinutes( now, 15 );
    const waiting = ( orders || [] )
        .filter( order => {
            if ( order.squarePaymentId === 'STORE' ) {
                return false;
            }
            const pickupTime = parseISO( order.pickupDateAndTime );
            if ( hours.open && pickupTime < endOfDay( now ) ) { // Check for orders that should have been picked up
                if ( pickupTime < hours.open || now > subMinutes( hours.close, 30 ) ) {
                    order.latePickup = true;
                    return true;
                }
            }
            if ( ! order.isPacked ) {
                if ( 
                    pickupTime < plusFifteenMinutes || 
                    ( ! order.wasSeen && isToday( pickupTime ) && isToday( parseISO( order.orderDate ) ) ) 
                ) {
                    return true;
                }
            }
            return false;
        } )
        .sort( sortMostUrgent );
    const wasWaiting = { ...alertCache.orders };
    alertCache.orders = {};
    for ( const order of waiting ) {
        const pickupTime = parseISO( order.pickupDateAndTime );
        if ( ! wasWaiting[ order._id ] ) {
            alertCache.lastPing = null;
        }
        alertCache.orders[ order._id ] = true;
        const [ messageClass, messagePrefix ] = (
            order.latePickup                ? [ classes.Overdue,        'NOT PICKED UP' ]   :
            pickupTime < now                ? [ classes.Overdue,        'Now' ]             :
            pickupTime < plusFiveMinutes    ? [ classes.FiveMinutes,    'Next 5 minutes' ]  :
            pickupTime < plusFifteenMinutes ? [ classes.FifteenMinutes, 'Next 15 minutes' ] :
                                              [ classes.NewOrder,       'New order' ]
        );
        const [ count, ready, packed ] = order.items
            .map( item => ( 
                { count: item.count, 
                  readyCount: item.readyCount || 0, 
                  packedCount: item.packedCount || 0 } ) )
            .reduce( 
                ( [ count, ready, packed ], counts ) => 
                    [ count + counts.count, ready + counts.readyCount, packed + counts.packedCount ], 
                [ 0 ,0, 0 ] 
            );
        messages.push( 
            <div key={ order._id } data-id={ order._id } className={ classes.AlertMessage } >
                <div className={ messageClass } >{ messagePrefix }:</div>
                Customer: <div className={ classes.PickupName } >{ order.pickupName || order.name }</div>
                Total Items: <div className={ classes.ItemCount } >{ count }</div>
                To Prep: <div className={ classes.ItemCount } >{ count - ready }</div>
                To Pack: <div className={ classes.ItemCount } >{ count - packed }</div>
                <Button onClick={ ( e ) => {
                            e.stopPropagation();
                            alertCache.lastPing = now; // Defer checking for changes for five minutes
                            setDetailOrderId( e.target.closest( 'div' ).dataset.id );
                        } } 
                    className={ classes.Details }
                >Details</Button>
                { messagePrefix === 'New order' &&
                    <div data-id={ order._id }>
                        <Button onClick={ markOrderAsSeen } className={ classes.NewButton }>OK</Button>
                    </div>
                }
            </div>
        );
    }
    if ( messages.length === 0 ) {
        setAlert( null );
        alertCache.lastPing = null;
    } else {
        if ( ! alertCache.lastPing || alertCache.lastPing < fiveMinutesAgo ) {
            try {
                document.getElementById( 'fernleaf-orders-chime' ).play();
                alertCache.lastPing = now;
            } catch ( e ) {}
        }
        setAlert( messages );
    }
}

const checkForAlerts = ( setAlert, setDetailOrderId, hours, dispatch ) => {
    if ( ! alertCache.checkPending ) {
        alertCache.checkPending = true;
        apiGetOrdersToPrep( 
            ( orders ) => {
                try {
                    checkOrders( orders, hours, setAlert, setDetailOrderId ); 
                    alertCache.checkPending = false;
                } catch (e) {
                    console.error( "checkForAlerts - error:", e );
                    // Probably got logged out.
                }
            },
            dummyDispatch
        );
    }
};

const Alerts = () => {
    const dispatch = useDispatch();
    const calendar = useSelector( getCalendar );
    const alertsToBack = useSelector( getAlertsToBack );
    const [ alert, setAlert ] = useState( null );
    const [ detailOrderId, setDetailOrderId ] = useState();
    const [ hours ] = useState( getHoursForToday( calendar ) );

    useEffect( () => {
        const timer = setInterval( () => checkForAlerts( setAlert, setDetailOrderId, hours, dispatch ), 5000 );

        return () => clearInterval( timer );
    }, [ hours, dispatch ] );

    const toggleAlert = () => {
        dispatch( setAlertsToBack( ! alertsToBack ) );
    }

    return ( alert &&
        <React.Fragment>
            <div className={ alertsToBack ? classes.Alerts : classes.AlertsFront } >
                <div className={ classes.Header } >
                    <div>
                        Orders Due Soon / New
                    </div>
                    <div>
                        <Button onClick={ toggleAlert } className={ classes.ToggleButton }>
                            { alertsToBack ? "SHOW" : "HIDE" }
                        </Button>
                    </div>
                </div>
                { alert }
            </div>
            { detailOrderId && 
                <Modal className={ classes.OrderDetailsFrame } bdcn={ classes.OrderDetailsBackdrop }
                    backdropClassName={ classes.OrderDetailsBackdrop }
                    onClick={ () => setDetailOrderId( null ) }
                >
                    <iframe title='ORDER-DETAILS'
                        src={ `${ window.adminRoot() }/admin/order-details?orderId=${ detailOrderId }` } 
                            className={ classes.OrderDetails }
                    />
                </Modal>
            }
        </React.Fragment>
    );
}

export default Alerts;